import { useState, type ReactNode } from 'react'
import { Lock, Pencil, Phone } from 'lucide-react'
import { toast } from 'sonner'
import { useUpdatePurchaseOrder } from '@/api/hooks'
import type { components } from '@/api/schema'
import { ChoicePicker } from '@/components/choice-picker'
import { StatusPill } from '@/components/status-pill'
import { useUpdatePurchaseOrder as useUpdatePurchaseOrderFields } from '@/features/purchase-orders/hooks'
import { InlineFieldGroup, type InlineGroupField } from '@/components/inline-field-group'
import { ActionPopover } from '@/components/action-popover'
import { useActorName } from '@/hooks/use-actor-name'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { formatApiErrorMessage } from '@/lib/api-error'
import { parseUTCDate } from '@/lib/utils'

type PurchaseOrder = components['schemas']['PurchaseOrderDetailRead']
type ShipToKey =
  | 'name'
  | 'company'
  | 'address1'
  | 'address2'
  | 'city'
  | 'state'
  | 'zip'
  | 'country'
  | 'phone'

const SHIP_TO_FIELDS: ReadonlyArray<InlineGroupField<ShipToKey>> = [
  { key: 'name', label: 'Name', autoComplete: 'name' },
  { key: 'company', label: 'Company', autoComplete: 'organization' },
  { key: 'address1', label: 'Address 1', required: true, autoComplete: 'address-line1' },
  { key: 'address2', label: 'Address 2', autoComplete: 'address-line2' },
  { key: 'city', label: 'City', required: true, autoComplete: 'address-level2' },
  { key: 'state', label: 'State', required: true, autoComplete: 'address-level1', className: 'col-span-2' },
  { key: 'zip', label: 'ZIP', required: true, autoComplete: 'postal-code', className: 'col-span-4' },
  { key: 'country', label: 'Country', required: true, autoComplete: 'country-name' },
  { key: 'phone', label: 'Phone', required: true, autoComplete: 'tel' },
]

function currentValues(po: PurchaseOrder): Record<ShipToKey, string> {
  return {
    name: po.ship_to_name ?? '',
    company: po.ship_to_company ?? '',
    address1: po.ship_to_address1 ?? '',
    address2: po.ship_to_address2 ?? '',
    city: po.ship_to_city ?? '',
    state: po.ship_to_state ?? '',
    zip: po.ship_to_zip ?? '',
    country: po.ship_to_country ?? '',
    phone: po.ship_to_phone ?? '',
  }
}

function AddressBlock({
  address,
  name,
}: {
  address: Record<string, string | null | undefined>
  name?: ReactNode
}) {
  const cityLine = [address.city, address.state].filter(Boolean).join(', ')
  const hasAddress = Object.values(address).some(Boolean)

  if (!hasAddress) {
    return (
      <p className="text-sm text-muted-foreground">
        No ship-to address recorded.
      </p>
    )
  }

  return (
    <div className="space-y-0.5 break-words text-sm">
      {(name || address.name) && (
        <p className="font-medium">{name ?? address.name}</p>
      )}
      {address.company && <p>{address.company}</p>}
      {address.address1 && <p>{address.address1}</p>}
      {address.address2 && <p>{address.address2}</p>}
      {(cityLine || address.zip) && (
        <p>
          {cityLine}
          {address.zip ? `${cityLine ? ' ' : ''}${address.zip}` : ''}
        </p>
      )}
      {address.country &&
        address.country !== 'US' &&
        address.country !== 'USA' && <p>{address.country}</p>}
      {address.phone && (
        <p className="flex items-start gap-1.5 pt-1 text-muted-foreground">
          <Phone className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{address.phone}</span>
        </p>
      )}
    </div>
  )
}

function shipmentBlocksShipToEdit(
  shipment: PurchaseOrder['shipments'][number],
) {
  if (shipment.status === 'cancelled') {
    return Boolean(shipment.actual_ship_date || shipment.shopify_fulfillment_id)
  }
  return Boolean(
    !['pending', 'ready_to_book', 'cancelled'].includes(shipment.status) ||
    shipment.booked_at ||
    shipment.actual_ship_date ||
    shipment.tracking_numbers ||
    shipment.pro_number ||
    shipment.bol_number ||
    shipment.booking_reference ||
    shipment.bol_pdf_filename ||
    shipment.label_pdf_filename,
  )
}

const DELIVERY_SITES = [
  { value: 'commercial', label: 'Commercial' },
  { value: 'residential', label: 'Residential' },
] as const

/** Residential or Commercial, as the PO prints it; picking one saves and regenerates the PO (USA-1465). */
function DeliverySitePicker({ po }: { po: PurchaseOrder }) {
  const mutation = useUpdatePurchaseOrderFields(po.uuid)
  const value =
    po.residential_delivery == null ? '' : po.residential_delivery ? 'residential' : 'commercial'
  const label = DELIVERY_SITES.find((site) => site.value === value)?.label
  return (
    <ChoicePicker
      title="Delivery site"
      label={`Delivery site: ${label ?? 'not set'}. Change`}
      value={value}
      choices={DELIVERY_SITES}
      pending={mutation.isPending}
      onSelect={(next) =>
        mutation.mutate(
          { accessorials: { residential_delivery: next === 'residential' } },
          {
            onSuccess: () =>
              toast.success(
                `Delivery site set to ${next === 'residential' ? 'Residential' : 'Commercial'} — PO regenerated`,
              ),
            onError: (error) =>
              toast.error(formatApiErrorMessage('Delivery site was not changed', error)),
          },
        )
      }
    >
      <StatusPill tone={label ? 'neutral' : 'warning'}>{label ?? 'Site not set'}</StatusPill>
    </ChoicePicker>
  )
}

interface POShipToEditorProps {
  po: PurchaseOrder
  effectiveName?: ReactNode
  /** Render as a section inside the PO details card instead of as its own card. */
  embedded?: boolean
}

export function POShipToEditor({ po, effectiveName, embedded = false }: POShipToEditorProps) {
  const mutation = useUpdatePurchaseOrder(po.uuid)
  const actorName = useActorName()
  const [editing, setEditing] = useState(false)
  // A booked shipment no longer blocks the change; it is made on purpose and Ops updates the carrier.
  const booked = po.shipments.some(shipmentBlocksShipToEdit)
  const effectiveAddress = {
    name: po.ship_to_name,
    company: po.ship_to_company,
    address1: po.ship_to_address1,
    address2: po.ship_to_address2,
    city: po.ship_to_city,
    state: po.ship_to_state,
    zip: po.ship_to_zip,
    country: po.ship_to_country,
    phone: po.ship_to_phone,
  }

  const save = (next: Record<ShipToKey, string>, reason: string) => {
    mutation.mutate(
      {
        ship_to_name: next.name.trim() || null,
        ship_to_company: next.company.trim() || null,
        ship_to_address1: next.address1.trim(),
        ship_to_address2: next.address2.trim() || null,
        ship_to_city: next.city.trim(),
        ship_to_state: next.state.trim(),
        ship_to_zip: next.zip.trim(),
        ship_to_country: next.country.trim(),
        ship_to_phone: next.phone.trim(),
        ship_to_override_reason: reason,
        ship_to_expected_revision: po.ship_to_override_revision,
        ...(booked ? { ship_to_override_after_booking: true } : {}),
      },
      {
        onSuccess: () => {
          setEditing(false)
          toast.success(
            booked
              ? 'Ship-to updated and PO PDF regenerated — Ops will update the carrier'
              : 'Ship-to address updated and PO PDF regenerated',
          )
        },
        onError: (error) =>
          toast.error(formatApiErrorMessage('Ship-to address was not updated', error)),
      },
    )
  }

  // Who changed the address, why, and what it was: one small click away, not always on screen.
  const overrideBadge = po.ship_to_is_overridden && !editing && (
    <ActionPopover
      align="end"
      title="Ship-to edited"
      trigger={
        <button
          type="button"
          className="rounded px-1 text-xs text-amber-700 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 dark:text-amber-400"
        >
          Edited
        </button>
      }
    >
      <div className="space-y-3 text-sm">
        {po.ship_to_override_reason && <p>{po.ship_to_override_reason}</p>}
        {po.ship_to_overridden_at && (
          <p className="text-xs text-muted-foreground">
            {parseUTCDate(po.ship_to_overridden_at).toLocaleString(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
            {po.ship_to_overridden_by ? ` · ${actorName(po.ship_to_overridden_by)}` : ''}
          </p>
        )}
        <div className="space-y-1 border-t pt-3">
          <p className="text-xs text-muted-foreground">Original address</p>
          <AddressBlock address={po.original_ship_to_snapshot} />
        </div>
      </div>
    </ActionPopover>
  )
  const changeButton = !editing && (
    <Button
      variant="ghost"
      size="icon"
      className="size-6 text-muted-foreground hover:text-foreground"
      onClick={() => setEditing(true)}
      aria-label="Change ship-to"
      title="Change ship-to"
    >
      <Pencil className="size-3.5" aria-hidden="true" />
    </Button>
  )
  const body = (
    <>
      <InlineFieldGroup
        label="Ship-to address"
        fields={SHIP_TO_FIELDS}
        values={currentValues(po)}
        interactiveChildren
        editing={editing}
        onEditingChange={setEditing}
        reason={{ label: 'Reason for change', placeholder: 'Reason for change' }}
        notice={
          booked && (
            <p className="flex items-start gap-1.5 text-amber-800 dark:text-amber-300">
              <Lock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
              Freight is booked with the old address. Ops gets an item to update the carrier.
            </p>
          )
        }
        saveLabel="Save ship-to"
        pending={mutation.isPending}
        onSave={save}
      >
        <AddressBlock address={effectiveAddress} name={effectiveName} />
      </InlineFieldGroup>
      {!editing && (
        <>
          <DeliverySitePicker po={po} />
        </>
      )}
    </>
  )

  if (embedded) {
    return (
      <section aria-label="Ship to" className="min-w-0 space-y-1">
        <div className="flex h-6 items-center gap-1">
          <h4 className="text-xs text-muted-foreground">Ship to</h4>
          {overrideBadge}
          <span className="ml-auto">{changeButton}</span>
        </div>
        <div className="space-y-3">{body}</div>
      </section>
    )
  }

  return (
    <Card>
      <CardHeader className="space-y-0 pb-3 pt-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Ship To
            </h3>
            {overrideBadge}
          </div>
          {changeButton}
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pb-4">{body}</CardContent>
    </Card>
  )
}
